<script setup lang="ts">
import { trackEvent } from '@@/bits/analytics'
import { CommandBarUserPropertyType, setUserProperty } from '@@/bits/command_bar'
import { dir } from '@@/bits/current_dir'
import window from '@@/bits/global'
import { __ } from '@@/bits/intl'
import { navigateTo } from '@@/bits/location'
import { isRegistered } from '@@/bits/user_model'
import { defineAsyncComponent } from '@@/bits/vue'
import OzBaseButton from '@@/library/v4/components/OzBaseButton.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import OzIconButton, {
  OzIconButtonColorScheme,
  OzIconButtonSizePreset,
} from '@@/library/v4/components/OzIconButton.vue'
import OzPlainButton, {
  OzPlainButtonColorScheme,
  OzPlainButtonSizePreset,
} from '@@/library/v4/components/OzPlainButton.vue'
import { getAnchorFromElement } from '@@/library/v4/components/OzPopoverModal.vue'
import SearchIconWithIndicator from '@@/library/v4/components/SearchIconWithIndicator.vue'
import { useExpandedPostStore } from '@@/pinia/expanded_post'
import { useSlideshowStore } from '@@/pinia/slideshow'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceActivityPanelStore } from '@@/pinia/surface_activity_panel'
import { useSurfaceAIChatStore } from '@@/pinia/surface_ai_chat_store'
import { useSurfaceContainerSizeStore } from '@@/pinia/surface_container_size'
import { useSurfaceDraftsStore } from '@@/pinia/surface_drafts'
import { useSurfaceFilterStore } from '@@/pinia/surface_filter'
import { useSurfaceFreezePadletPanel } from '@@/pinia/surface_freeze_padlet_panel'
import { useSurfaceGuestStore } from '@@/pinia/surface_guest_store'
import { useSurfaceMapStore } from '@@/pinia/surface_map'
import { useSurfaceOnboardingDemoPadletPanelStore } from '@@/pinia/surface_onboarding_demo_padlet_panel_store'
import { useSurfacePanelsStore } from '@@/pinia/surface_panels'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostSearchStore } from '@@/pinia/surface_post_search'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfaceSettingsStore } from '@@/pinia/surface_settings'
import { useSurfaceShareLinksStore } from '@@/pinia/surface_share_links'
import { useWindowSizeStore } from '@@/pinia/window_size'
import type { PopoverAnchor, PostAttributes } from '@@/types'
import AppBarAccountAvatar from '@@/vuecomponents/AppBarAccountAvatar.vue'
import type { Button } from '@@/vuecomponents/SurfaceAddPostMenu.vue'
import SurfaceGuestIdLabel from '@@/vuecomponents/SurfaceGuestIdLabel.vue'
import { useSurfaceButtonSkinBasedTwClasses } from '@@/vuecomposables/surface_button_skin_based_tw_classes'
import { useSurfaceNavbarVisibility } from '@@/vuecomposables/surface_navbar_visibility'
import type { LongPressEvent } from '@@/vuedirectives/emit_long_press'
import vEmitLongPress from '@@/vuedirectives/emit_long_press'
import { debounce } from 'es-toolkit'
import { storeToRefs } from 'pinia'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

const MagicChatIconButton = defineAsyncComponent(() => import('@@/vuecomponents/MagicChatIconButton.vue'))
const SurfaceAddPostMenu = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceAddPostMenu.vue'))
const SurfaceOnboardingDemoPadletPulsatingWrapper = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfaceOnboardingDemoPadletPulsatingWrapper.vue'),
)

const POPOVER_WIDTH = 256
const ONBOARDING_PANEL_OFFSET = POPOVER_WIDTH + 13
const moreButton = ref<InstanceType<typeof OzIconButton>>()

const surfaceStore = useSurfaceStore()
const surfaceGuestStore = useSurfaceGuestStore()
const surfaceDraftsStore = useSurfaceDraftsStore()
const surfaceContainerSizeStore = useSurfaceContainerSizeStore()
const surfaceSettingsStore = useSurfaceSettingsStore()
const { isContainerSmallerThanTabletPortrait, isContainerSmallerThanTabletLandscape } =
  storeToRefs(surfaceContainerSizeStore)
const {
  user,
  isMap,
  links,
  loginAndRedirectToWallUrl,
  isDesktopLayout,
  isMobileLayout,
  skinPrimaryVariantColorAsRGB,
  isSkinColorAvailable,
  isSkinPrimaryColorDark,
  wallAttributes,
  isFrozen,
  hasSidePanelOutsideSurface,
  onboardingDemoButtonStyle,
} = storeToRefs(surfaceStore)
const { showPromptToLoginDialog } = surfaceStore
const surfacePermissionsStore = useSurfacePermissionsStore()
const { canIAdminister, canIPost, canIRemake, canIAccessContentDirectly, amICollaborator } =
  storeToRefs(surfacePermissionsStore)

const { toggleSettingsPanel } = surfaceSettingsStore
const { xSurfaceActivityPanel } = storeToRefs(useSurfacePanelsStore())

const { activityPanelDisabled } = useSurfaceNavbarVisibility()

const { startPickingLocation } = useSurfaceMapStore()
const { startNewPost } = useSurfacePostsStore()
const { showSurfaceFreezePadletPanel } = useSurfaceFreezePadletPanel()
const { coverPageUrl } = storeToRefs(useSlideshowStore())

const surfaceShareLinksStore = useSurfaceShareLinksStore()
const { isBreakout } = storeToRefs(surfaceShareLinksStore)

const { isSmallerThanTabletPortrait, isSmallerThanTabletLandscape } = storeToRefs(useWindowSizeStore())
const surfaceAIChatStore = useSurfaceAIChatStore()
const { toggleChatPanel } = surfaceAIChatStore
const { xSurfaceAIChatPanel } = storeToRefs(surfaceAIChatStore)

const { isRemakeable } = storeToRefs(surfaceSettingsStore)

const { fetchActivityData } = useSurfaceActivityPanelStore()

const { buttonSkinBasedTailwindClasses, buttonSkinBasedTailwindClassesWithoutHover } =
  useSurfaceButtonSkinBasedTwClasses()

const expandedPostStore = useExpandedPostStore()
const { xExpandedPostPanel } = storeToRefs(expandedPostStore)

const surfaceDemoPadletPanelStore = useSurfaceOnboardingDemoPadletPanelStore()
const { isDemoPadletPanel, isDemoPadletPanelDesktop, demoPadletPanelVisibility, isShareStep, isPostStep } =
  storeToRefs(surfaceDemoPadletPanelStore)

const isDemoPadletPanelDesktopAndPostStep = computed<boolean>(() => isDemoPadletPanelDesktop.value && isPostStep.value)

const isDemoPadletPanelDesktopAndShareStep = computed<boolean>(
  () => isDemoPadletPanelDesktop.value && isShareStep.value,
)

const useSkinBasedColorsForButtons = computed<boolean>(() => {
  return isSkinColorAvailable.value && !isMobileLayout.value
})

const buttonColorScheme = computed(() => {
  if (isBreakout.value) {
    return OzIconButtonColorScheme.Transparent
  }

  if (useSkinBasedColorsForButtons.value) {
    return OzIconButtonColorScheme.Bare
  }

  return isMobileLayout.value ? OzIconButtonColorScheme.SecondaryClear : OzIconButtonColorScheme.Secondary
})

const activeButtonColorScheme = computed<OzIconButtonColorScheme>(() => {
  if (useSkinBasedColorsForButtons.value) {
    return OzIconButtonColorScheme.Bare
  }

  return isMobileLayout.value ? OzIconButtonColorScheme.SecondaryClearActive : OzIconButtonColorScheme.SecondaryActive
})

const activeButtonSkinBasedTailwindClasses = computed(() => {
  if (!useSkinBasedColorsForButtons.value) {
    return {}
  }

  // isSkinPrimaryColorDark is either true or false here (cannot be 'auto') since we checked for isSkinColorAvailable
  return {
    // Base
    'bg-grey-1000 bg-opacity-5': isSkinPrimaryColorDark.value === false,
    'bg-white-100 bg-opacity-10': isSkinPrimaryColorDark.value === true,
    'text-grape-500': isSkinPrimaryColorDark.value === false,
    'text-canary-500': isSkinPrimaryColorDark.value === true,

    // Hovered
    'hover-hover:hover:bg-opacity-10': isSkinPrimaryColorDark.value === false,
    'hover-hover:hover:bg-opacity-15': isSkinPrimaryColorDark.value === true,

    // Disabled
    'aria-disabled:!bg-highway-400': isSkinPrimaryColorDark.value === false,
    'aria-disabled:!bg-white-disabled': isSkinPrimaryColorDark.value === true,
  }
})

const isUserRegistered = computed<boolean>(() => {
  return isRegistered(user.value)
})

const isRemakeDisabled = computed<boolean>(() => {
  if (!isUserRegistered.value) {
    // Check isRemakeable as canIRemake is always false for unregistered users
    // and we want the remake button to be clickable (for padlets with remakes enabled)
    // so the login dialog can be shown
    return !isRemakeable.value
  }
  return !canIRemake.value || !canIAccessContentDirectly.value
})

/**
 * MOBILE
 * - [ Share, Activity, +, Search, ... ]
 *
 *
 * DESKTOP
 * - [ Share, Remake, Activity, Slideshow, Settings, ... ]
 */

const { searchTerm } = storeToRefs(useSurfacePostSearchStore())
const { hasSelectedFilters } = storeToRefs(useSurfaceFilterStore())

const xSearchIndicator = computed<boolean>(() => {
  return searchTerm.value !== '' || hasSelectedFilters.value
})

function showSharePanel() {
  if (isDemoPadletPanelDesktopAndShareStep.value) {
    surfaceDemoPadletPanelStore.handleStartShareAnimation()
  } else {
    useSurfacePanelsStore().xSurfaceSharePanel = true
  }
}

function showSearchBar() {
  useSurfaceStore().openSearchBar()
}

function openSlideshow() {
  trackEvent('Surface', 'Opened slideshow')
  if (!links.value.show) {
    return
  }

  navigateTo(coverPageUrl.value, { target: '_blank' })

  // set to true so we don't show the nudge to users who have already created a slideshow
  setUserProperty(CommandBarUserPropertyType.HasCreatedSlideshowBefore, true)
}

function showRequestLoginToRemakeDialog(): void {
  showPromptToLoginDialog({
    title: __('You must be logged in to remake this padlet'),
  })
}

function handleRemakeClick(): void {
  if (isUserRegistered.value) {
    useSurfacePanelsStore().xSurfaceRemakePanel = true
  } else {
    showRequestLoginToRemakeDialog()
  }
}

const isLoginAndRedirectToWallUrlExpired = ref(false)

/**
 * SHOW/HIDE MORE MENU
 */

function openMoreMenu(): void {
  if (!moreMenuAnchor.value.top || !moreMenuAnchor.value.left) {
    updateMoreMenuAnchor()
  }
  useSurfacePanelsStore().showActionBarMoreMenu()
}

/**
 * MORE MENU POSITIONING
 */

const moreMenuAnchor = ref<Partial<PopoverAnchor>>({})

function updateMoreMenuAnchor(): void {
  const moreButtonEl = moreButton.value?.$el
  if (!moreButtonEl) return
  const anchor = getAnchorFromElement(moreButtonEl, {
    top: 0,
    left: isMobileLayout.value
      ? dir() === 'rtl'
        ? -15
        : hasSidePanelOutsideSurface.value
          ? 12 - 160 // numbers by experimentation
          : 12
      : dir() === 'rtl'
        ? hasSidePanelOutsideSurface.value
          ? -12 + ONBOARDING_PANEL_OFFSET
          : -12
        : hasSidePanelOutsideSurface.value
          ? -39 - ONBOARDING_PANEL_OFFSET
          : -39,
  })
  moreMenuAnchor.value = {
    ...anchor,
    position: 'side',
  }
}

const debouncedUpdateMoreMenuAnchor = debounce(() => {
  if (window.visualViewport != null && window.visualViewport.scale > 1) {
    // When the content is pinch-zoomed, prevented any resizing and recalculation.
    return
  }
  updateMoreMenuAnchor()
}, 500)

const addPostButtonRef = ref<InstanceType<typeof OzBaseButton>>()
const xAddPostMenu = ref(false)

function handleAddPostClick(): void {
  if (isMap.value) {
    startPickingLocation()
  } else {
    if (surfaceDemoPadletPanelStore.isDemoPadletPanelDesktop && surfaceDemoPadletPanelStore.isPostStep) {
      surfaceDemoPadletPanelStore.setFavoritePlacesSectionAsMostRecentlyTouched()
      // Limit to one post draft at a time
      if (!surfaceDraftsStore.isAnyDraftActive) {
        import('@@/pinia/surface_onboarding_demo_padlet_panel_copy_store').then(
          ({ useSurfaceOnboardingDemoPadletPanelCopyStore }) => {
            const surfaceDemoPadletPanelCopyStore = useSurfaceOnboardingDemoPadletPanelCopyStore()
            const attributes: PostAttributes = {
              ...surfaceDemoPadletPanelCopyStore.postData[0],
            }
            startNewPost({ attributes })
          },
        )
      }
    } else {
      startNewPost({})
    }
  }
}

const { xAiChat } = storeToRefs(surfaceAIChatStore)
function handleAddPostLongPress(e: LongPressEvent): void {
  e.preventDefault()
  if (!xAiChat.value) return
  xAddPostMenu.value = true
}

const addPostMenuButtons: Button[] = [
  {
    text: __('Write a post'),
    ariaLabel: __('Write a post'),
    icon: 'pencil_outline',
    action: () => {
      startNewPost({})
    },
  },
  {
    text: surfaceAIChatStore.assistantName,
    ariaLabel: surfaceAIChatStore.assistantName,
    icon: 'sparkles_outline',
    action: () => {
      toggleAIChatPanel()
    },
  },
]

const isSharePanelDisabled = computed(() => {
  // Allow collaborators to access share panel when content protection is "Disable all"
  // This enables them to leave the padlet and access limited sharing features
  if (amICollaborator.value && !canIAccessContentDirectly.value) {
    return false
  }
  return !canIAccessContentDirectly.value
})

const toggleAIChatPanel = (): void => {
  trackEvent(
    'Surface AI chat',
    xSurfaceAIChatPanel.value ? 'Closed surface AI chat panel' : 'Opened surface AI chat panel',
    null,
    null,
    { source: 'Action bar' },
  )
  toggleChatPanel()
}

/**
 * COMPONENT LIFECYCLE
 */

onMounted(async () => {
  if (loginAndRedirectToWallUrl.value) {
    // Refer to DEEPLINK_RESPONSE_WINDOW_DURATION_WITH_BUFFER in app/services/lti_launch_context.rb
    const TIMEOUT_MS = 15 * 60 * 1000
    setTimeout(() => (isLoginAndRedirectToWallUrlExpired.value = true), TIMEOUT_MS)
  }
  window.addEventListener('resize', debouncedUpdateMoreMenuAnchor)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', debouncedUpdateMoreMenuAnchor)
})

// #region Event tracking
const trackGuestIdLabelClick = (): void => {
  trackEvent('SurfaceActionBar', 'Clicked guest id label', null, null, { target: 'surface-action-bar-guest-id-label' })
}

const openFreezePanel = (): void => {
  showSurfaceFreezePadletPanel()
  trackEvent('SurfaceActionBar', 'Opened freeze panel', null, null, { target: 'surface-action-bar-open-freeze-panel' })
}
// #endregion
</script>

<template>
  <div id="surface-action-bar">
    <div
      :class="[
        'flex items-center',
        isMobileLayout
          ? [
              'justify-between @tablet-portrait/surface-container:justify-around',
              'space-s-6 @tablet-portrait/surface-container:space-s-0',
              'pt-1 pb-[max(0.25rem,env(safe-area-inset-bottom,0.25rem))]',
              'px-1',
            ]
          : ['flex-col space-y-4 justify-start', 'box-border h-full py-4'],
        {
          'bg-light-ui-100 dark:bg-dark-ui-100': !isSkinColorAvailable,
        },
        {
          'rounded-b-3xl': hasSidePanelOutsideSurface && isSmallerThanTabletPortrait && !isDemoPadletPanel,
        },
      ]"
      :style="isSkinColorAvailable ? { backgroundColor: skinPrimaryVariantColorAsRGB } : {}"
      data-testid="surfaceActionBar"
    >
      <SurfaceGuestIdLabel
        v-if="
          surfaceGuestStore.shouldEnableAnonymousAttribution &&
          surfaceGuestStore.isAttributionEnabled &&
          !isUserRegistered &&
          !xExpandedPostPanel &&
          !isSmallerThanTabletLandscape &&
          demoPadletPanelVisibility.showSurfaceActionBarButtons
        "
        :class="['self-end', 'me-4']"
        @click="trackGuestIdLabelClick"
      />
      <AppBarAccountAvatar
        v-else-if="!isMobileLayout"
        :user="user"
        :referrer-url="wallAttributes.links ? wallAttributes.links.show : undefined"
        :dark-mode="isSkinPrimaryColorDark"
        :disabled="false"
      />

      <!-- Share button -->
      <SurfaceOnboardingDemoPadletPulsatingWrapper
        v-if="demoPadletPanelVisibility.showModifiedSurfaceActionBarShareButton"
        :is-smaller-than-tablet-portrait="isContainerSmallerThanTabletPortrait"
        :has-square-shape="true"
      >
        <OzIconButton
          v-if="demoPadletPanelVisibility.showSurfaceActionBarButtons"
          :label="__('Open share panel')"
          icon-name="share_arrow_outline"
          :disabled="isBreakout || isDemoPadletPanelDesktopAndShareStep || isSharePanelDisabled"
          :icon-size="isMobileLayout ? 24 : 16"
          :size-preset="OzIconButtonSizePreset.H40px"
          :color-scheme="buttonColorScheme"
          :style="onboardingDemoButtonStyle"
          :dark-mode="isSkinPrimaryColorDark"
          :class="[
            'grow @tablet-portrait/surface-container:grow-0',
            buttonSkinBasedTailwindClasses,
            demoPadletPanelVisibility.showModifiedSurfaceActionBarShareButton && 'text-light-text-100',
          ]"
          data-testid="surfaceShareButton"
          data-pepin-id="surface-action-bar-show-share-panel"
          data-pepin-trigger="click"
          @click="showSharePanel"
        />
      </SurfaceOnboardingDemoPadletPulsatingWrapper>
      <template v-else>
        <OzIconButton
          v-if="demoPadletPanelVisibility.showSurfaceActionBarButtons"
          :label="__('Open share panel')"
          icon-name="share_arrow_outline"
          :disabled="
            isBreakout ||
            isDemoPadletPanelDesktopAndPostStep ||
            isDemoPadletPanelDesktopAndShareStep ||
            isSharePanelDisabled
          "
          :icon-size="isMobileLayout ? 24 : 16"
          :size-preset="OzIconButtonSizePreset.H40px"
          :color-scheme="buttonColorScheme"
          :dark-mode="isSkinPrimaryColorDark"
          :class="[
            'grow @tablet-portrait/surface-container:grow-0',
            buttonSkinBasedTailwindClasses,
            demoPadletPanelVisibility.showModifiedSurfaceActionBarShareButton && 'text-light-text-100',
          ]"
          data-testid="surfaceShareButton"
          data-pepin-id="surface-action-bar-show-share-panel"
          data-pepin-trigger="click"
          @click="showSharePanel"
        />
      </template>

      <!-- [Desktop] Remake button -->
      <OzIconButton
        v-if="isDesktopLayout && demoPadletPanelVisibility.showSurfaceActionBarButtons"
        :label="isRemakeDisabled ? __('Remakes disabled') : __('Remake this padlet')"
        :disabled="
          isRemakeDisabled || isBreakout || isDemoPadletPanelDesktopAndPostStep || isDemoPadletPanelDesktopAndShareStep
        "
        icon-name="arrow_2_rectangular_clockwise_thin"
        :icon-size="isMobileLayout ? 24 : 16"
        :size-preset="OzIconButtonSizePreset.H40px"
        :color-scheme="buttonColorScheme"
        :dark-mode="isSkinPrimaryColorDark"
        :class="['grow @tablet-portrait/surface-container:grow-0', buttonSkinBasedTailwindClasses]"
        data-testid="surfaceRemakePanelButton"
        :data-pepin-id="isUserRegistered && 'surface-action-bar-show-remake-panel'"
        data-pepin-trigger="click"
        @click="handleRemakeClick"
      />

      <!-- [Desktop] activity panel button -->
      <OzIconButton
        v-if="isDesktopLayout && demoPadletPanelVisibility.showSurfaceActionBarButtons"
        :label="
          activityPanelDisabled
            ? __('Activity panel is disabled')
            : xSurfaceActivityPanel
              ? __('Close activity panel')
              : __('Open activity panel')
        "
        :icon-name="xSurfaceActivityPanel ? 'bell_filled' : 'bell'"
        :icon-size="isMobileLayout ? 24 : 16"
        :disabled="
          isBreakout ||
          isDemoPadletPanelDesktopAndPostStep ||
          isDemoPadletPanelDesktopAndShareStep ||
          activityPanelDisabled
        "
        :size-preset="OzIconButtonSizePreset.H40px"
        :color-scheme="xSurfaceActivityPanel ? activeButtonColorScheme : buttonColorScheme"
        :dark-mode="isSkinPrimaryColorDark"
        :class="[
          'grow @tablet-portrait/surface-container:grow-0',
          xSurfaceActivityPanel ? activeButtonSkinBasedTailwindClasses : buttonSkinBasedTailwindClasses,
        ]"
        :data-pepin-id="
          xSurfaceActivityPanel ? 'surface-action-bar-close-activity-panel' : 'surface-action-bar-open-activity-panel'
        "
        data-testid="activityPanelButton"
        data-pepin-trigger="click"
        @click="() => (xSurfaceActivityPanel = !xSurfaceActivityPanel)"
        @mouseenter="fetchActivityData"
        @focus="fetchActivityData"
      />

      <!-- [Mobile] activity panel button -->
      <OzIconButton
        v-if="isMobileLayout"
        :label="
          activityPanelDisabled
            ? __('Activity panel is disabled')
            : xSurfaceActivityPanel
              ? __('Close activity panel')
              : __('Open activity panel')
        "
        :icon-name="xSurfaceActivityPanel ? 'bell_filled' : 'bell'"
        :icon-size="24"
        :disabled="
          isBreakout ||
          isDemoPadletPanelDesktopAndPostStep ||
          isDemoPadletPanelDesktopAndShareStep ||
          activityPanelDisabled
        "
        :size-preset="OzIconButtonSizePreset.H40px"
        :color-scheme="xSurfaceActivityPanel ? activeButtonColorScheme : buttonColorScheme"
        :dark-mode="isSkinPrimaryColorDark"
        :class="[
          'grow @tablet-portrait/surface-container:grow-0',
          xSurfaceActivityPanel ? activeButtonSkinBasedTailwindClasses : buttonSkinBasedTailwindClasses,
        ]"
        :data-pepin-id="
          xSurfaceActivityPanel ? 'surface-action-bar-close-activity-panel' : 'surface-action-bar-open-activity-panel'
        "
        data-testid="activityPanelButton"
        data-pepin-trigger="click"
        @click="xSurfaceActivityPanel = !xSurfaceActivityPanel"
        @mouseenter="fetchActivityData"
        @focus="fetchActivityData"
      />

      <!-- [Mobile] Add post button -->
      <SurfaceOnboardingDemoPadletPulsatingWrapper
        v-if="isMobileLayout && demoPadletPanelVisibility.showModifiedSurfaceAddPostButton"
        :is-smaller-than-tablet-portrait="isContainerSmallerThanTabletPortrait"
      >
        <OzBaseButton
          v-if="canIPost && isMobileLayout && !isFrozen"
          ref="addPostButtonRef"
          v-emit-long-press
          :class="[
            'grow @tablet-portrait/surface-container:grow-0',
            'flex items-center justify-center',
            // The icon size is 32x32 so we need to
            // add 4px padding to both sides to make
            // it as wide as the other 40x40 buttons.
            'px-1',
            'bg-transparent',
            'focus-visible:ring-2.5 rounded-xl',
            isSkinPrimaryColorDark ? 'focus-visible:ring-canary-500' : 'focus-visible:ring-grape-500',
          ]"
          :aria-label="__('Add post')"
          :title="__('Add post')"
          data-testid="surfaceAddPostButton"
          data-pw="surfaceAddPostButton"
          :data-pepin-id="isMap ? 'surface-action-bar-open-location-picker' : 'surface-action-bar-open-post-composer'"
          data-pepin-trigger="click"
          @click="handleAddPostClick"
          @long-press="handleAddPostLongPress($event)"
        >
          <div
            :class="[
              'flex items-center justify-center',
              'rounded-full w-8 h-8',
              { 'bg-padlet-pink text-light-text-100 hover-hover:hover:bg-padlet-pink-600': !isSkinColorAvailable },
            ]"
            :style="onboardingDemoButtonStyle"
          >
            <OzIcon name="plus_thick" :size="20" />
          </div>
        </OzBaseButton>
      </SurfaceOnboardingDemoPadletPulsatingWrapper>
      <template v-else>
        <OzBaseButton
          v-if="canIPost && isMobileLayout && !isFrozen"
          ref="addPostButtonRef"
          v-emit-long-press
          :class="[
            'grow @tablet-portrait/surface-container:grow-0',
            'flex items-center justify-center',
            // The icon size is 32x32 so we need to
            // add 4px padding to both sides to make
            // it as wide as the other 40x40 buttons.
            'p-1',
            'bg-transparent',
            'focus-visible:ring-2.5 rounded-xl',
            isSkinPrimaryColorDark ? 'focus-visible:ring-canary-500' : 'focus-visible:ring-grape-500',
          ]"
          :aria-label="__('Add post')"
          :title="__('Add post')"
          :disabled="isDemoPadletPanelDesktopAndPostStep || isDemoPadletPanelDesktopAndShareStep"
          data-testid="surfaceAddPostButton"
          data-pw="surfaceAddPostButton"
          :data-pepin-id="isMap ? 'surface-action-bar-open-location-picker' : 'surface-action-bar-open-post-composer'"
          data-pepin-trigger="click"
          @click="handleAddPostClick"
          @long-press="handleAddPostLongPress($event)"
        >
          <div
            :class="[
              'flex items-center justify-center',
              'rounded-full w-8 h-8',
              { 'bg-padlet-pink text-light-text-100 hover-hover:hover:bg-padlet-pink-600': !isSkinColorAvailable },
            ]"
            :style="onboardingDemoButtonStyle"
          >
            <OzIcon name="plus_thick" :size="20" />
          </div>
        </OzBaseButton>
      </template>

      <!-- [Mobile] search button -->
      <OzPlainButton
        v-if="isMobileLayout"
        :disabled="isDemoPadletPanelDesktopAndPostStep || isDemoPadletPanelDesktopAndShareStep"
        :aria-label="__('Open search bar')"
        :size-preset="OzPlainButtonSizePreset.H44px"
        :color-scheme="OzPlainButtonColorScheme.SecondaryIcon"
        :dark-mode="isSkinPrimaryColorDark"
        :class="['grow @tablet-portrait/surface-container:grow-0', buttonSkinBasedTailwindClasses]"
        data-testid="surfaceSearchButton"
        :data-pepin-id="'surface-action-bar-open-search-bar'"
        data-pepin-trigger="click"
        @click="showSearchBar()"
      >
        <template #icon>
          <SearchIconWithIndicator
            v-if="xSearchIndicator"
            :dark-mode="isSkinPrimaryColorDark"
            color-shade="text-200"
            :size="24"
          />
          <OzIcon v-else name="search" :size="24" />
        </template>
      </OzPlainButton>

      <!-- [Desktop] slideshow button -->
      <!-- Should not show when smaller than tablet landscape because slideshow buttons are not shown at all -->
      <SurfaceOnboardingDemoPadletPulsatingWrapper
        v-if="
          !isContainerSmallerThanTabletLandscape &&
          demoPadletPanelVisibility.showModifiedSurfaceActionBarSlideshowButton
        "
        :is-smaller-than-tablet-portrait="isContainerSmallerThanTabletPortrait"
        :has-square-shape="true"
      >
        <OzIconButton
          v-if="!isMobileLayout && demoPadletPanelVisibility.showSurfaceActionBarButtons"
          :label="__('Open slideshow')"
          icon-name="play_outline"
          :icon-size="16"
          :disabled="isBreakout || isDemoPadletPanelDesktopAndShareStep"
          :size-preset="OzIconButtonSizePreset.H40px"
          :color-scheme="buttonColorScheme"
          :dark-mode="isSkinPrimaryColorDark"
          :class="[
            'grow-0',
            buttonSkinBasedTailwindClasses,
            demoPadletPanelVisibility.showModifiedSurfaceActionBarSlideshowButton && 'text-light-text-100',
          ]"
          :style="onboardingDemoButtonStyle"
          :data-pepin-id="'surface-action-bar-open-slideshow'"
          data-testid="surfaceSlideshowButton"
          data-pepin-trigger="click"
          @click="openSlideshow"
        />
      </SurfaceOnboardingDemoPadletPulsatingWrapper>
      <template v-else>
        <OzIconButton
          v-if="!isMobileLayout && demoPadletPanelVisibility.showSurfaceActionBarButtons"
          :label="__('Open slideshow')"
          icon-name="play_outline"
          :icon-size="16"
          :disabled="isBreakout || isDemoPadletPanelDesktopAndPostStep || isDemoPadletPanelDesktopAndShareStep"
          :size-preset="OzIconButtonSizePreset.H40px"
          :color-scheme="buttonColorScheme"
          :dark-mode="isSkinPrimaryColorDark"
          :class="[
            'grow-0',
            buttonSkinBasedTailwindClasses,
            demoPadletPanelVisibility.showModifiedSurfaceActionBarSlideshowButton && 'text-light-text-100',
          ]"
          :data-pepin-id="'surface-action-bar-open-slideshow'"
          data-testid="surfaceSlideshowButton"
          data-pepin-trigger="click"
          @click="openSlideshow"
        />
      </template>

      <!-- [Desktop] Freeze panel / wall settings panel button -->
      <template v-if="canIAdminister && !isMobileLayout && demoPadletPanelVisibility.showSurfaceActionBarButtons">
        <SurfaceOnboardingDemoPadletPulsatingWrapper
          v-if="
            !isContainerSmallerThanTabletPortrait &&
            demoPadletPanelVisibility.showModifiedSurfaceActionBarSettingsButton
          "
          :is-smaller-than-tablet-portrait="isContainerSmallerThanTabletPortrait"
          :has-square-shape="true"
        >
          <OzIconButton
            v-if="isFrozen"
            :label="__('Open freeze padlet panel')"
            icon-name="freeze"
            :icon-size="isMobileLayout ? 24 : 16"
            :size-preset="OzIconButtonSizePreset.H40px"
            :color-scheme="buttonColorScheme"
            :dark-mode="isSkinPrimaryColorDark"
            :class="[
              'grow @tablet-portrait/surface-container:grow-0',
              buttonSkinBasedTailwindClasses,
              demoPadletPanelVisibility.showModifiedSurfaceActionBarSettingsButton && 'text-light-text-100',
            ]"
            @click="openFreezePanel"
          />
          <OzIconButton
            v-else
            :label="__('Open padlet settings')"
            icon-name="gear_outline"
            :icon-size="isMobileLayout ? 24 : 16"
            :disabled="isBreakout || isDemoPadletPanelDesktopAndShareStep"
            :size-preset="OzIconButtonSizePreset.H40px"
            :color-scheme="buttonColorScheme"
            :dark-mode="isSkinPrimaryColorDark"
            :class="[
              'grow @tablet-portrait/surface-container:grow-0',
              buttonSkinBasedTailwindClasses,
              demoPadletPanelVisibility.showModifiedSurfaceActionBarSettingsButton && 'text-light-text-100',
            ]"
            :style="onboardingDemoButtonStyle"
            data-testid="surfaceSettingsButton"
            data-pepin-id="surface-action-bar-show-settings-panel"
            data-pepin-trigger="click"
            @click="toggleSettingsPanel()"
          />
        </SurfaceOnboardingDemoPadletPulsatingWrapper>
        <template v-else>
          <OzIconButton
            v-if="isFrozen"
            :label="__('Open freeze padlet panel')"
            icon-name="freeze"
            :icon-size="isMobileLayout ? 24 : 16"
            :size-preset="OzIconButtonSizePreset.H40px"
            :color-scheme="buttonColorScheme"
            :dark-mode="isSkinPrimaryColorDark"
            :class="[
              'grow @tablet-portrait/surface-container:grow-0',
              buttonSkinBasedTailwindClasses,
              demoPadletPanelVisibility.showModifiedSurfaceActionBarSettingsButton && 'text-light-text-100',
            ]"
            @click="openFreezePanel"
          />
          <OzIconButton
            v-else
            :label="__('Open padlet settings')"
            icon-name="gear_outline"
            :icon-size="isMobileLayout ? 24 : 16"
            :disabled="isBreakout || isDemoPadletPanelDesktopAndPostStep || isDemoPadletPanelDesktopAndShareStep"
            :size-preset="OzIconButtonSizePreset.H40px"
            :color-scheme="buttonColorScheme"
            :dark-mode="isSkinPrimaryColorDark"
            :class="[
              'grow @tablet-portrait/surface-container:grow-0',
              buttonSkinBasedTailwindClasses,
              demoPadletPanelVisibility.showModifiedSurfaceActionBarSettingsButton && 'text-light-text-100',
            ]"
            data-testid="surfaceSettingsButton"
            data-pepin-id="surface-action-bar-show-settings-panel"
            data-pepin-trigger="click"
            @click="toggleSettingsPanel()"
          />
        </template>
      </template>

      <!-- More menu button -->
      <OzIconButton
        v-if="demoPadletPanelVisibility.showSurfaceActionBarButtons"
        ref="moreButton"
        :label="__('More padlet actions')"
        :icon-name="'dot_3_circle_outline'"
        :icon-size="24"
        :size-preset="OzIconButtonSizePreset.H40px"
        :color-scheme="
          isDemoPadletPanelDesktopAndPostStep || isDemoPadletPanelDesktopAndShareStep
            ? OzIconButtonColorScheme.Transparent
            : OzIconButtonColorScheme.SecondaryClear
        "
        :dark-mode="isSkinPrimaryColorDark"
        :class="'grow @tablet-portrait/surface-container:grow-0'"
        data-testid="surfaceMoreButton"
        :disabled="isDemoPadletPanelDesktopAndPostStep || isDemoPadletPanelDesktopAndShareStep"
        data-pw="surfaceMoreButton"
        data-pepin-id="surface-action-bar-open-more-menu"
        data-pepin-trigger="click"
        @click="openMoreMenu"
      />
      <div v-if="isDesktopLayout && xAiChat" class="absolute bottom-[107px] end-0 group">
        <!-- Shadows used to simulate the rounded edges when the button is clicked -->
        <svg
          v-if="xSurfaceAIChatPanel"
          class="absolute start-10 bottom-11 rtl:-scale-x-100"
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            class="fill-light-ui-100 dark:fill-dark-ui-100 shadow-light-ui-100 dark:shadow-dark-ui-100"
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M0
          12C6.62742 12 12 6.62742 12 0V12H0Z"
            fill="none"
          />
        </svg>
        <svg
          v-if="xSurfaceAIChatPanel"
          class="absolute start-10 top-11 rtl:-scale-x-100"
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            class="fill-light-ui-100 dark:fill-dark-ui-100 shadow-light-ui-100 dark:shadow-dark-ui-100"
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M0 0C6.62742 0 12 5.37258 12 12V0H0Z"
            fill="none"
          />
        </svg>
        <!-- Button skin classes should only be applied when the panel is not open -->
        <MagicChatIconButton
          :class="['relative', !xSurfaceAIChatPanel && buttonSkinBasedTailwindClassesWithoutHover]"
          :is-active="xSurfaceAIChatPanel"
          :dark-mode="isSkinPrimaryColorDark"
          :color-scheme="buttonColorScheme"
          :aria-label="__('AI teaching assistant chat')"
          aria-haspopup="dialog"
          @click="toggleAIChatPanel"
        />
      </div>
    </div>

    <SurfaceAddPostMenu
      v-if="xAddPostMenu && addPostButtonRef && demoPadletPanelVisibility.showSurfaceAiChat"
      :popover-anchor="addPostButtonRef.$el"
      :buttons="addPostMenuButtons"
      @close-menu="xAddPostMenu = false"
    />
  </div>
</template>
